/**
 * =============================================================================
 * Hook: useAffiliateAuditTrail
 * Versão: 1.0.0 (Fase 8 — Bloco 8.6 / 8.7)
 *
 * Descrição:
 *   Consome a RPC `get_affiliate_audit_trail` para listar o histórico de
 *   alterações em entidades do módulo de Indicação. Apenas administradores
 *   conseguem executar (a própria RPC valida).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useAffiliateAuditTrail');

export interface AffiliateAuditEntry {
  id: string;
  tenant_id: string;
  affiliate_id: string | null;
  affiliate_name: string | null;
  entity_type: string;
  entity_id: string | null;
  action: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  performed_by: string | null;
  performed_by_name: string | null;
  ip_address: string | null;
  created_at: string;
  total_count: number;
}

export interface AffiliateAuditFilters {
  affiliateId?: string | null;
  entityType?: string | null;
  action?: string | null;
  limit?: number;
  offset?: number;
}

export function useAffiliateAuditTrail(filters: AffiliateAuditFilters = {}) {
  const {
    affiliateId = null,
    entityType = null,
    action = null,
    limit = 50,
    offset = 0,
  } = filters;

  return useQuery({
    queryKey: [
      'affiliate-audit-trail',
      affiliateId,
      entityType,
      action,
      limit,
      offset,
    ],
    queryFn: async (): Promise<{
      rows: AffiliateAuditEntry[];
      total: number;
    }> => {
      const { data, error } = await supabase.rpc('get_affiliate_audit_trail', {
        p_affiliate_id: affiliateId,
        p_entity_type: entityType,
        p_action: action,
        p_limit: limit,
        p_offset: offset,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('Erro ao buscar trilha de auditoria', error);
        throw error;
      }

      const rows = (data ?? []) as AffiliateAuditEntry[];
      const total = rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0;
      return { rows, total };
    },
    staleTime: 30_000,
  });
}
